import React, { useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, FormControlLabel, Grid, MenuItem, TextField, Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import pocketsafeCustomersApi from 'config/api/pocketsafeCustomers/pocketsafeCustomers';
import customer_messages from 'messages/customer_messages';
import general_messages from 'messages/general_messages';

type CustomerMessage = readonly string[];

type LanguageOption = {
  language_code: string;
  label: string;
};

type ValueOption = {
  value: string;
  label: string;
};

type CustomerFormState = {
  organization: {
    name: string;
    organization_number: string;
    country: string;
    business_type: string;
    organization_size: string;
    force_duplicate_name: boolean;
  };
  owner: {
    first_name: string;
    last_name: string;
    email: string;
    language_code: string;
  };
  administratorEnabled: boolean;
  administrator: {
    first_name: string;
    last_name: string;
    email: string;
    language_code: string;
  };
};

type FieldErrors = Record<string, string>;

type DialogProps = {
  onClose: () => void;
  onCreated: () => void;
  open: boolean;
};

type ApiErrorResponse = {
  field?: string;
  user_message?: string;
  detail?: string;
};

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const createInitialState = (): CustomerFormState => ({
  organization: {
    name: '',
    organization_number: '',
    country: 'SE',
    business_type: '',
    organization_size: '',
    force_duplicate_name: false,
  },
  owner: {
    first_name: '',
    last_name: '',
    email: '',
    language_code: '',
  },
  administratorEnabled: false,
  administrator: {
    first_name: '',
    last_name: '',
    email: '',
    language_code: '',
  },
});

const PocketSafeCustomerOnboardingDialog = ({ onClose, onCreated, open }: DialogProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const translate = (message: CustomerMessage) => t(message[0], message[1] || '');
  const [form, setForm] = useState<CustomerFormState>(createInitialState);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [businessTypes, setBusinessTypes] = useState<ValueOption[]>([]);
  const [organizationSizes, setOrganizationSizes] = useState<ValueOption[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    let mounted = true;

    pocketsafeCustomersApi.getLanguages().then((data: LanguageOption[]) => {
      if (mounted) setLanguages(data);
    });
    pocketsafeCustomersApi.getBusinessTypes().then((data: ValueOption[]) => {
      if (mounted) setBusinessTypes(data);
    });
    pocketsafeCustomersApi.getOrganizationSizes().then((data: ValueOption[]) => {
      if (mounted) setOrganizationSizes(data);
    });

    return () => {
      mounted = false;
    };
  }, []);

  const languageOptions = useMemo<LanguageOption[]>(() => [{ language_code: '', label: '-' }, ...languages], [languages]);

  const setOrganizationValue = (field: keyof CustomerFormState['organization'], value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      organization: {
        ...prev.organization,
        [field]: value,
      },
    }));
  };

  const setOwnerValue = (field: keyof CustomerFormState['owner'], value: string) => {
    setForm(prev => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: value,
      },
    }));
  };

  const setAdministratorValue = (field: keyof CustomerFormState['administrator'], value: string) => {
    setForm(prev => ({
      ...prev,
      administrator: {
        ...prev.administrator,
        [field]: value,
      },
    }));
  };

  const validate = (): boolean => {
    const nextErrors: FieldErrors = {};

    if (!form.organization.name.trim()) nextErrors['organization.name'] = 'Required';
    if (!form.organization.organization_number.trim()) nextErrors['organization.organization_number'] = 'Required';
    if (!form.owner.first_name.trim()) nextErrors['owner.first_name'] = 'Required';
    if (!form.owner.last_name.trim()) nextErrors['owner.last_name'] = 'Required';
    if (!form.owner.email.trim()) nextErrors['owner.email'] = 'Required';

    if (form.administratorEnabled) {
      if (!form.administrator.first_name.trim()) nextErrors['administrator.first_name'] = 'Required';
      if (!form.administrator.last_name.trim()) nextErrors['administrator.last_name'] = 'Required';
      if (!form.administrator.email.trim()) nextErrors['administrator.email'] = 'Required';
    }

    if (
      form.administratorEnabled &&
      form.owner.email.trim().toLowerCase() &&
      form.owner.email.trim().toLowerCase() === form.administrator.email.trim().toLowerCase()
    ) {
      nextErrors['administrator.email'] = 'Owner and administrator must use different email addresses.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setErrorMessage('');

    try {
      await pocketsafeCustomersApi.onboard({
        organization: form.organization,
        owner: form.owner,
        administrator: form.administratorEnabled ? form.administrator : null,
      });
      onCreated();
    } catch (error: unknown) {
      const responseData = axios.isAxiosError(error) ? (error.response?.data as ApiErrorResponse | undefined) : undefined;

      if (responseData?.field && responseData?.user_message) {
        const fieldKey = String(responseData.field);
        const userMessage = responseData.user_message;

        setFieldErrors(prev => ({ ...prev, [fieldKey]: userMessage }));
        setErrorMessage(userMessage);
      } else {
        setErrorMessage(responseData?.detail || translate(general_messages.api_error));
      }
    } finally {
      setSaving(false);
    }
  };

  const renderOrganizationFieldError = (field: keyof CustomerFormState['organization']) => fieldErrors[`organization.${field}`] || ' ';
  const renderOwnerFieldError = (field: keyof CustomerFormState['owner']) => fieldErrors[`owner.${field}`] || ' ';
  const renderAdministratorFieldError = (field: keyof CustomerFormState['administrator']) => fieldErrors[`administrator.${field}`] || ' ';

  return (
    <DialogGrid
      color='secondary'
      dialogActions={
        <>
          <Button disabled={saving} onClick={onClose}>
            {translate(customer_messages.cancel)}
          </Button>
          <Button color='secondary' disabled={saving} onClick={handleSubmit} variant='contained'>
            {translate(customer_messages.confirm)}
          </Button>
        </>
      }
      displayTitle
      fullWidth
      loading={false}
      loadingCover={false}
      noPadding={false}
      onClose={onClose}
      open={open}
      scrollDisabled={false}
      sending={saving}
      title={translate(customer_messages.onboarding_dialog_title)}
      withCloseConfirmation={false}
    >
      {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

      <section className={styles.section}>
        <Typography className={styles.sectionTitle} variant='h4'>
          {translate(customer_messages.organization_section)}
        </Typography>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <TextField
              error={Boolean(fieldErrors['organization.name'])}
              fullWidth
              helperText={renderOrganizationFieldError('name')}
              label={translate(customer_messages.organization)}
              onChange={event => setOrganizationValue('name', event.target.value)}
              size='small'
              value={form.organization.name}
              variant='outlined'
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              error={Boolean(fieldErrors['organization.organization_number'])}
              fullWidth
              helperText={renderOrganizationFieldError('organization_number')}
              label={translate(customer_messages.organization_number)}
              onChange={event => setOrganizationValue('organization_number', event.target.value)}
              size='small'
              value={form.organization.organization_number}
              variant='outlined'
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.country)}
              onChange={event => setOrganizationValue('country', event.target.value)}
              select
              size='small'
              value={form.organization.country}
              variant='outlined'
            >
              <MenuItem value='SE'>Sweden</MenuItem>
              <MenuItem value='O'>Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.business_type)}
              onChange={event => setOrganizationValue('business_type', event.target.value)}
              select
              size='small'
              value={form.organization.business_type}
              variant='outlined'
            >
              <MenuItem value=''>-</MenuItem>
              {businessTypes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.organization_size)}
              onChange={event => setOrganizationValue('organization_size', event.target.value)}
              select
              size='small'
              value={form.organization.organization_size}
              variant='outlined'
            >
              <MenuItem value=''>-</MenuItem>
              {organizationSizes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.organization.force_duplicate_name}
                  onChange={event => setOrganizationValue('force_duplicate_name', event.target.checked)}
                />
              }
              label={translate(customer_messages.force_duplicate_name)}
            />
          </Grid>
        </Grid>
      </section>

      <section className={styles.section}>
        <Typography className={styles.sectionTitle} variant='h4'>
          {translate(customer_messages.owner_section)}
        </Typography>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <TextField
              error={Boolean(fieldErrors['owner.first_name'])}
              fullWidth
              helperText={renderOwnerFieldError('first_name')}
              label={translate(general_messages.first_name as CustomerMessage)}
              onChange={event => setOwnerValue('first_name', event.target.value)}
              size='small'
              value={form.owner.first_name}
              variant='outlined'
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              error={Boolean(fieldErrors['owner.last_name'])}
              fullWidth
              helperText={renderOwnerFieldError('last_name')}
              label={translate(general_messages.last_name as CustomerMessage)}
              onChange={event => setOwnerValue('last_name', event.target.value)}
              size='small'
              value={form.owner.last_name}
              variant='outlined'
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              error={Boolean(fieldErrors['owner.email'])}
              fullWidth
              helperText={renderOwnerFieldError('email')}
              label={translate(general_messages.email as CustomerMessage)}
              onChange={event => setOwnerValue('email', event.target.value)}
              size='small'
              value={form.owner.email}
              variant='outlined'
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.language)}
              onChange={event => setOwnerValue('language_code', event.target.value)}
              select
              size='small'
              value={form.owner.language_code}
              variant='outlined'
            >
              {languageOptions.map(option => (
                <MenuItem key={option.language_code || 'default'} value={option.language_code}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </section>

      <section className={styles.section}>
        <Typography className={styles.sectionTitle} variant='h4'>
          {translate(customer_messages.administrator_section)}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.administratorEnabled}
              onChange={event => setForm(prev => ({ ...prev, administratorEnabled: event.target.checked }))}
            />
          }
          label={translate(customer_messages.add_administrator)}
        />
        {form.administratorEnabled && (
          <Grid container spacing={2}>
            <Grid item sm={6} xs={12}>
              <TextField
                error={Boolean(fieldErrors['administrator.first_name'])}
                fullWidth
                helperText={renderAdministratorFieldError('first_name')}
                label={translate(general_messages.first_name as CustomerMessage)}
                onChange={event => setAdministratorValue('first_name', event.target.value)}
                size='small'
                value={form.administrator.first_name}
                variant='outlined'
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                error={Boolean(fieldErrors['administrator.last_name'])}
                fullWidth
                helperText={renderAdministratorFieldError('last_name')}
                label={translate(general_messages.last_name as CustomerMessage)}
                onChange={event => setAdministratorValue('last_name', event.target.value)}
                size='small'
                value={form.administrator.last_name}
                variant='outlined'
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                error={Boolean(fieldErrors['administrator.email'])}
                fullWidth
                helperText={renderAdministratorFieldError('email')}
                label={translate(general_messages.email as CustomerMessage)}
                onChange={event => setAdministratorValue('email', event.target.value)}
                size='small'
                value={form.administrator.email}
                variant='outlined'
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                fullWidth
                label={translate(customer_messages.language)}
                onChange={event => setAdministratorValue('language_code', event.target.value)}
                select
                size='small'
                value={form.administrator.language_code}
                variant='outlined'
              >
                {languageOptions.map(option => (
                  <MenuItem key={option.language_code || 'default'} value={option.language_code}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}
      </section>
    </DialogGrid>
  );
};

export default PocketSafeCustomerOnboardingDialog;
